import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { Chip, EmptyState, InfiniteSentinel, Skeletons, RM, dt } from '../components/kit';
import { S } from '../lib/space';
import * as coShareApi from '../api/coShare';

/**
 * FULL-SCREEN · /co-share — both sides of internal co-share, in one screen.
 *
 * Two boxes rather than two pages, because they are the same object seen from
 * either end and an agent is routinely on both ends at once:
 *
 *   Requests to me  — my listing, someone else's buyer. Mine to answer, and once
 *                     accepted, mine to raise the sale from.
 *   My requests     — someone else's listing, my buyer. Mine to wait on.
 *
 * The status filter is deliberately absent: this list is short by nature (a request
 * is answered in days, not months) and both boxes lead with what needs doing.
 */

const BOXES = [
  { key: 'incoming', label: 'Requests to me', sub: 'on your listings' },
  { key: 'outgoing', label: 'My requests', sub: 'on other agents’ listings' },
];

const TONE = { PENDING: 'warn', ACCEPTED: 'ok', REJECTED: 'bad', WITHDRAWN: 'muted', CONVERTED: 'brand' };
// The status word alone is ambiguous across the two boxes ("accepted" by whom?), so
// each row says what the state means from the side the reader is on.
const LINE = {
  incoming: {
    PENDING: 'Waiting on you',
    ACCEPTED: 'Accepted — raise the sale',
    REJECTED: 'You declined',
    WITHDRAWN: 'Pulled',
    CONVERTED: 'Sale raised',
  },
  outgoing: {
    PENDING: 'Waiting on them',
    ACCEPTED: 'Accepted — they raise the sale',
    REJECTED: 'Declined',
    WITHDRAWN: 'Pulled',
    CONVERTED: 'Sale raised',
  },
};

export default function CoSharePage() {
  const nav = useNavigate();
  const [box, setBox] = useState('incoming');
  const params = useMemo(() => ({ box }), [box]);
  const list = usePaginatedList(coShareApi.listCoShareRequests, { limit: 10, params });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Co-share" sub="internal co-broke requests" />

      <div style={{ display: 'flex', gap: 10, padding: `14px ${S.page}px`, background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        {BOXES.map((b) => {
          const active = b.key === box;
          return (
            <div
              key={b.key}
              data-tour={`cs-box-${b.key}`}
              onClick={() => setBox(b.key)}
              style={{ flex: 1, textAlign: 'center', padding: '11px 8px', borderRadius: 12, cursor: 'pointer', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{b.label}</div>
              <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>{b.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: S.page, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.loading ? (
          <Skeletons n={4} height={108} />
        ) : list.empty ? (
          <EmptyState
            title={box === 'incoming' ? 'Nothing waiting on you' : 'You haven’t asked for a co-share yet'}
            sub={box === 'incoming'
              ? 'When another agent has a buyer for one of your listings, their request lands here.'
              : 'Open another agent’s listing from the Listings tab and send a co-share request — that is how you get onto their deal.'}
          />
        ) : (
          <>
            {list.items.map((r, i) => {
              const other = box === 'incoming' ? r.requester : r.lister;
              const l = r.listing || {};
              return (
                <div
                  key={r.id}
                  data-tour={i === 0 ? 'cs-request-row' : undefined}
                  className="tap"
                  onClick={() => nav(`/co-share/${r.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nav(`/co-share/${r.id}`); } }}
                  style={{ display: 'flex', gap: 14, padding: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: S.radius, cursor: 'pointer' }}
                >
                  {l.cover_image_url ? (
                    <img src={l.cover_image_url} alt="" style={{ width: 68, height: 68, borderRadius: 12, flex: 'none', objectFit: 'cover', border: '1px solid var(--border)' }} />
                  ) : (
                    <div style={{ width: 68, height: 68, borderRadius: 12, flex: 'none', background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 6px,var(--chip) 6px,var(--chip) 12px)', border: '1px solid var(--border)' }} />
                  )}

                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, minWidth: 0 }}>{l.property_title || l.listing_ref || 'Listing'}</div>
                      <Chip label={r.status} tone={TONE[r.status]} />
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'ui-monospace,monospace' }}>{l.listing_ref}</span>
                      {[l.area, l.state].filter(Boolean).length ? ` · ${[l.area, l.state].filter(Boolean).join(', ')}` : ''}
                      {l.asking_price_rm ? ` · ${RM(l.asking_price_rm)}` : ''}
                    </div>
                    <div style={{ fontSize: 11.5 }}>
                      <span style={{ color: 'var(--muted)' }}>{box === 'incoming' ? 'From ' : 'To '}</span>
                      <span style={{ fontWeight: 600 }}>{other?.name || 'CID agent'}</span>
                      {(r.agreed_split_percentage ?? r.proposed_split_percentage) != null && (
                        <span style={{ color: 'var(--muted)' }}>
                          {' · '}{r.agreed_split_percentage != null ? 'agreed' : 'proposed'}{' '}
                          {r.agreed_split_percentage ?? r.proposed_split_percentage}%
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: r.status === 'PENDING' ? 'var(--warn)' : r.status === 'ACCEPTED' ? 'var(--ok)' : 'var(--muted)' }}>
                        {LINE[box][r.status] || r.status}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{dt(r.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}
        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}
